import clsx from "clsx";
import { FC, useMemo } from "react";

import {
  ConfigUpdate,
  EarlyStoppingSummary,
  EvalSpec,
  EvalStats,
} from "@tsmono/inspect-common/types";
import { MetaDataGrid, RecordTree } from "@tsmono/inspect-components/content";
import { Card, CardBody, CardHeader } from "@tsmono/react/components";
import { formatNumber, ghCommitUrl, toTitleCase } from "@tsmono/util";

import { getApi } from "../../../app_config";
import { kLogViewTaskTabId } from "../../../constants";
import { formatDateTime, formatDuration } from "../../../utils/format";
import { TagsField } from "../title-view/TagsField";

import { ConfigCard } from "./ConfigCard";
import styles from "./TaskTab.module.css";

// Individual hook for Info tab
export const useTaskTabConfig = (
  evalSpec: EvalSpec | undefined,
  evalStats?: EvalStats,
  earlyStopping?: EarlyStoppingSummary | null,
  tags?: string[],
  configUpdates?: ConfigUpdate[] | null
) => {
  return useMemo(() => {
    return {
      id: kLogViewTaskTabId,
      label: "Task",
      scrollable: true,
      component: TaskTab,
      componentProps: {
        evalSpec,
        evalStats,
        earlyStopping,
        tags,
        configUpdates,
      },
    };
  }, [evalSpec, evalStats, earlyStopping, tags, configUpdates]);
};

interface TaskTabProps {
  evalSpec?: EvalSpec;
  evalStats?: EvalStats;
  earlyStopping?: EarlyStoppingSummary | null;
  tags?: string[];
  configUpdates?: ConfigUpdate[] | null;
}

export const TaskTab: FC<TaskTabProps> = ({
  evalSpec,
  evalStats,
  earlyStopping,
  tags,
  configUpdates,
}) => {
  // Only used to decide whether to include the "tags" row in the
  // metadata grid for an empty log — TagsField owns the actual gating
  // (in-progress, dialog state, save flow, refresh).
  const api = getApi();
  const canEditTags = Boolean(api.edit_log);
  const tagList = tags ?? [];

  const revision = evalSpec?.revision;
  const packages = evalSpec?.packages;

  const taskInformation: Record<string, unknown> = {
    ["Task ID"]: evalSpec?.task_id,
    ["Run ID"]: evalSpec?.run_id,
  };

  if (revision) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const revisionKey = `${revision.type ? `${toTitleCase(revision.type)} ` : ""}Revision`;
    const commitUrl = ghCommitUrl(revision.origin, revision.commit);
    taskInformation[revisionKey] = commitUrl
      ? { _html: <a href={commitUrl}>{revision.commit}</a> }
      : revision.commit;
  }
  if (packages) {
    const names = Object.keys(packages).map((key) => {
      return `${key} ${packages[key]}`;
    });

    if (names.length === 1) {
      taskInformation["Inspect"] = names[0];
    } else {
      taskInformation["Inspect"] = names;
    }
  }
  // Mirror the header's chip strip in the Task Info card — saving from
  // either surface refreshes the log so both views stay in sync.
  // `MetaDataGrid` renders the `_html` payload as raw JSX, bypassing its
  // default string formatting.
  if (tagList.length > 0 || canEditTags) {
    taskInformation["tags"] = {
      _html: <TagsField tags={tagList} className={styles.tagPillAlign} />,
    };
  }

  if (evalSpec?.sandbox) {
    if (Array.isArray(evalSpec.sandbox)) {
      taskInformation["sandbox"] = evalSpec.sandbox[0];
      if (evalSpec.sandbox[1]) {
        taskInformation["sandbox_config"] = evalSpec.sandbox[1];
      }
    } else {
      taskInformation["sandbox"] = evalSpec.sandbox.type;
      taskInformation["sandbox_config"] = evalSpec.sandbox.config;
    }
  }

  const totalDuration = formatDuration(
    new Date(evalStats?.started_at || 0),
    new Date(evalStats?.completed_at || 0)
  );

  const task_args = evalSpec?.task_args || {};

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          padding: "0.5em 1em 0 1em",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
        }}
      >
        <Card>
          <CardHeader label="Task Info" />
          <CardBody id={"task-card-config"}>
            <div className={clsx(styles.grid)}>
              <MetaDataGrid
                key={`plan-md-task`}
                className={"text-size-small"}
                entries={taskInformation}
                options={{ copyButton: true }}
              />

              <MetaDataGrid
                entries={{
                  ["Start"]: formatDateTime(
                    new Date(evalStats?.started_at || 0)
                  ),
                  ["End"]: formatDateTime(
                    new Date(evalStats?.completed_at || 0)
                  ),
                  ["Duration"]: totalDuration,
                }}
                options={{ copyButton: true }}
              />
            </div>
          </CardBody>
        </Card>

        <ConfigCard config={evalSpec?.config} configUpdates={configUpdates} />

        {earlyStopping && (
          <Card>
            <CardHeader
              label={`Early Stopping (${earlyStopping.manager} — ${formatNumber(earlyStopping.early_stops.length)} skipped)`}
            />
            <CardBody>
              <RecordTree
                id={`early-stopping-metadata`}
                record={earlyStopping.metadata}
                copyButton={true}
              />
            </CardBody>
          </Card>
        )}

        {Object.keys(task_args).length > 0 && (
          <Card>
            <CardHeader label="Task Args" />
            <CardBody id={"task-card-config"}>
              <MetaDataGrid
                key={`plan-md-task-args`}
                className={"text-size-small"}
                entries={task_args}
              />
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};
